import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faVoteYea, faPlus, faTrash, faArrowRight, faCircleInfo,
  faCircleCheck, faCircleHalfStroke, faPenToSquare, faUsers
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { getElections, createElection, deleteElection } from '../services/api';
import './Elections.css';

const statusLabel = {
  draft: { text: 'Rascunho', cls: 'st-draft' },
  open: { text: 'Em andamento', cls: 'st-open' },
  closed: { text: 'Encerrada', cls: 'st-closed' }
};

const Elections = () => {
  const { isAdmin, isSecretary } = useAuth();
  const navigate = useNavigate();
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    expectedVoters: '',
    rule: 'absolute_majority',
    showLiveResults: false
  });

  useEffect(() => {
    if (!isAdmin && !isSecretary) {
      navigate('/');
      return;
    }
    load();
  }, [isAdmin, isSecretary, navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await getElections();
      setElections(data);
    } catch (e) {
      toast.error('Erro ao carregar as votações.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.warn('Informe um título para a assembleia.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await createElection({
        title: form.title.trim(),
        expectedVoters: parseInt(form.expectedVoters) || 0,
        rule: form.rule,
        showLiveResults: form.showLiveResults
      });
      toast.success('Assembleia criada com os ministérios ativos.');
      setShowModal(false);
      setForm({ title: '', expectedVoters: '', rule: 'absolute_majority', showLiveResults: false });
      navigate(`/admin/votacoes/${data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao criar a assembleia.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Excluir a assembleia "${title}"? Esta ação remove todos os dados de votação.`)) return;
    try {
      await deleteElection(id);
      toast.success('Assembleia excluída.');
      load();
    } catch (e) {
      toast.error('Erro ao excluir a assembleia.');
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1><FontAwesomeIcon icon={faVoteYea} /> Votações de Nomeação</h1>
          <button onClick={() => setShowModal(true)} className="btn btn-primary" title="Criar uma nova assembleia de votação">
            <FontAwesomeIcon icon={faPlus} /> Nova Assembleia
          </button>
        </div>

        <div className="elec-intro">
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>
            Crie uma assembleia, cadastre os candidatos de cada ministério e conduza a votação ao vivo.
            Os membros votam pelo celular usando um link único e o resultado aparece no telão.
          </span>
        </div>

        {elections.length === 0 ? (
          <div className="card empty-state">
            <FontAwesomeIcon icon={faVoteYea} className="empty-icon" />
            <p>Nenhuma votação criada ainda.</p>
            <button onClick={() => setShowModal(true)} className="btn btn-primary">
              <FontAwesomeIcon icon={faPlus} /> Criar a primeira assembleia
            </button>
          </div>
        ) : (
          <div className="elec-grid">
            {elections.map((e) => {
              const st = statusLabel[e.status] || statusLabel.draft;
              return (
                <div key={e._id} className="elec-card" onClick={() => navigate(`/admin/votacoes/${e._id}`)}>
                  <div className="elec-card-top">
                    <span className={`elec-status ${st.cls}`}>{st.text}</span>
                    <span className="elec-code" title="Código da sessão para o link de votação">{e.publicCode}</span>
                  </div>
                  <h3>{e.title}</h3>
                  <div className="elec-meta">
                    <span title="Ministérios decididos"><FontAwesomeIcon icon={faCircleCheck} /> {e.decidedMinistries}/{e.totalMinistries} ministérios</span>
                    {e.expectedVoters > 0 && (
                      <span title="Votantes esperados"><FontAwesomeIcon icon={faUsers} /> {e.expectedVoters} esperados</span>
                    )}
                  </div>
                  <div className="elec-card-actions">
                    <button
                      className="btn btn-sm btn-danger"
                      title="Excluir assembleia"
                      onClick={(ev) => { ev.stopPropagation(); handleDelete(e._id, e.title); }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                    <button className="btn btn-sm btn-primary" title="Abrir painel da assembleia">
                      Abrir <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2><FontAwesomeIcon icon={faVoteYea} /> Nova Assembleia</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label">
                      <FontAwesomeIcon icon={faPenToSquare} /> Título *
                      <span className="info-hint" data-tip="Ex.: Nomeações 2027. Aparece no telão e no celular dos votantes.">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Ex.: Assembleia de Nomeações 2027"
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FontAwesomeIcon icon={faUsers} /> Votantes esperados
                      <span className="info-hint" data-tip="Número de membros presentes. Serve para acompanhar quem ainda falta votar (ex.: 41 de 47).">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={form.expectedVoters}
                      onChange={(e) => setForm({ ...form, expectedVoters: e.target.value })}
                      placeholder="Pode preencher/ajustar depois"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FontAwesomeIcon icon={faCircleHalfStroke} /> Regra do resultado
                      <span className="info-hint" data-tip="Maioria absoluta: vencedor precisa de mais de 50% dos votos. Maioria simples: basta ser o mais votado.">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </span>
                    </label>
                    <select
                      className="form-control"
                      value={form.rule}
                      onChange={(e) => setForm({ ...form, rule: e.target.value })}
                    >
                      <option value="absolute_majority">Maioria absoluta (mais de 50%)</option>
                      <option value="simple_majority">Maioria simples (o mais votado)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={form.showLiveResults}
                        onChange={(e) => setForm({ ...form, showLiveResults: e.target.checked })}
                      />
                      <span>Mostrar votos parciais por candidato durante a votação</span>
                    </label>
                    <small className="form-help">Por padrão, apenas o total de votos aparece antes de encerrar (evita influenciar).</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Criando...' : 'Criar Assembleia'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Elections;
